import { z } from 'zod';
import { HI_TYPES, PROTOCOL_VERSION } from './constants';
import { DEVICE_ID_PATTERN } from './devices';
import { concatBytes, utf8 } from './encoding';

/**
 * What an envelope carries, as far as the relay needs to know for routing.
 * The content itself is encrypted and the server never sees it.
 *
 * sync: between devices of one account.
 * prescription: doctor to patient or pharmacy, or patient forwarding to the pharmacy they picked.
 * dispense_notice: a dispensing practice's record holder to the patient and the prescribing doctor.
 * care_record: doctor or clinical practice to patient, a copy of a record.
 * history_share: patient or another practice to a practice's record holder, under an approved consent.
 * practice_request and practice_response: a member's device and their practice's record holder.
 */
export const ENVELOPE_KINDS = [
  'sync',
  'prescription',
  'dispense_notice',
  'care_record',
  'history_share',
  'practice_request',
  'practice_response',
] as const;
export type EnvelopeKind = (typeof ENVELOPE_KINDS)[number];

/** Largest ciphertext in one envelope. Attachments in phase 12 may need a separate path. */
export const ENVELOPE_MAX_CIPHERTEXT_BYTES = 256 * 1024;
/** Most envelopes in one send request, for example one per device of the recipient. */
export const ENVELOPE_MAX_BATCH = 10;
/** Most undelivered envelopes one device may have waiting. */
export const MAX_PENDING_ENVELOPES_PER_DEVICE = 5000;
/** Longest encoded header accepted. */
export const ENVELOPE_HEADER_MAX_BYTES = 1024;
/** How far ahead of the server's clock a sender's createdAt may be. */
export const ENVELOPE_CLOCK_SKEW_MS = 10 * 60_000;

/** A zod schema for base64url text of exactly this many bytes. */
export function base64urlOf(bytes: number) {
  return z.string().regex(new RegExp(`^[A-Za-z0-9_-]{${Math.ceil((bytes * 4) / 3)}}$`), `Must be ${bytes} bytes`);
}

const objectId = z.string().regex(/^[a-f0-9]{24}$/, 'Must be an ID');

const addressSchema = z.strictObject({
  device: z.string().regex(DEVICE_ID_PATTERN, 'Must be a device ID'),
  /** Set when the device acts as this practice's record holder. */
  practice: objectId.optional(),
});
export type EnvelopeAddress = z.infer<typeof addressSchema>;

export const envelopeHeaderSchema = z.strictObject({
  v: z.literal(PROTOCOL_VERSION),
  /** 16 random bytes chosen by the sender. The relay refuses a repeated ID. */
  id: base64urlOf(16),
  kind: z.enum(ENVELOPE_KINDS),
  from: addressSchema,
  to: addressSchema,
  /** The approved consent a history share relies on. */
  consent: objectId.optional(),
  /** ABDM health information type of the content. Required for history shares. */
  hiType: z.enum(HI_TYPES).optional(),
  /** The sender's one-time ECDH P-256 public key for this envelope. */
  epk: base64urlOf(65),
  /** AES-GCM nonce. */
  iv: base64urlOf(12),
  createdAt: z.iso.datetime(),
});
export type EnvelopeHeader = z.infer<typeof envelopeHeaderSchema>;

function canonicalAddress(address: EnvelopeAddress): EnvelopeAddress {
  return address.practice === undefined
    ? { device: address.device }
    : { device: address.device, practice: address.practice };
}

/**
 * Encodes a header as compact JSON with keys in a fixed order. These bytes
 * are the AES-GCM additional data and part of what the sender signs.
 */
export function encodeEnvelopeHeader(header: EnvelopeHeader): Uint8Array<ArrayBuffer> {
  const ordered: Record<string, unknown> = {
    v: header.v,
    id: header.id,
    kind: header.kind,
    from: canonicalAddress(header.from),
    to: canonicalAddress(header.to),
  };
  if (header.consent !== undefined) ordered.consent = header.consent;
  if (header.hiType !== undefined) ordered.hiType = header.hiType;
  ordered.epk = header.epk;
  ordered.iv = header.iv;
  ordered.createdAt = header.createdAt;
  return utf8(JSON.stringify(ordered));
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * Reads header bytes and checks every field. Only the canonical encoding is
 * accepted, so the relay and the recipient cannot read different values from
 * the same bytes, for example through duplicate keys.
 */
export function decodeEnvelopeHeader(bytes: Uint8Array): EnvelopeHeader {
  if (bytes.length > ENVELOPE_HEADER_MAX_BYTES) throw new Error('The envelope header is too long');
  const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  const header = envelopeHeaderSchema.parse(JSON.parse(text));
  if (!bytesEqual(encodeEnvelopeHeader(header), bytes)) {
    throw new Error('The envelope header is not in canonical form');
  }
  return header;
}

/** What the sending device signs: a domain label, the header length, the header, then the ciphertext. */
export function envelopeSigningBytes(header: Uint8Array, ciphertext: Uint8Array): Uint8Array<ArrayBuffer> {
  const length = new Uint8Array(4);
  new DataView(length.buffer).setUint32(0, header.length);
  return concatBytes(utf8('curentia/v1/envelope\0'), length, header, ciphertext);
}
